import { useCallback, useEffect, useRef, useState } from "react";
import "./chat-image-zoom.css";

const ZOOM_MIN = 1;
const ZOOM_MAX = 6;

function clamp(n: number, a: number, b: number): number {
  return Math.min(b, Math.max(a, n));
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

type Props = {
  src: string;
  alt?: string;
  className?: string;
};

/** Zoom molette / pincement / curseur, déplacement une fois agrandi — comme le Coffre. */
export function ChatImageZoom({ src, alt = "", className }: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    dragRef.current = null;
    pinchRef.current = null;
    pointersRef.current.clear();
  }, [src]);

  const applyZoom = useCallback((nextZoom: number, clientX: number, clientY: number) => {
    const z0 = zoomRef.current;
    const z1 = clamp(nextZoom, ZOOM_MIN, ZOOM_MAX);
    if (z1 <= ZOOM_MIN) {
      zoomRef.current = ZOOM_MIN;
      panRef.current = { x: 0, y: 0 };
      setZoom(ZOOM_MIN);
      setPan({ x: 0, y: 0 });
      return;
    }
    const el = viewportRef.current;
    const rect = el?.getBoundingClientRect();
    const cx = rect ? clientX - rect.left - rect.width / 2 : 0;
    const cy = rect ? clientY - rect.top - rect.height / 2 : 0;
    const p0 = panRef.current;
    const ox = (cx - p0.x) / z0;
    const oy = (cy - p0.y) / z0;
    const p1 = { x: cx - ox * z1, y: cy - oy * z1 };
    zoomRef.current = z1;
    panRef.current = p1;
    setZoom(z1);
    setPan(p1);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.88 : 1.14;
      applyZoom(zoomRef.current * factor, e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [applyZoom]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button != null && e.button !== 0) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size >= 2) {
      const pts = [...pointersRef.current.values()];
      const a = pts[0]!;
      const b = pts[1]!;
      pinchRef.current = { dist: Math.max(1, dist(a, b)), zoom: zoomRef.current };
      dragRef.current = null;
      setDragging(false);
      return;
    }
    if (zoomRef.current <= ZOOM_MIN) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pinchRef.current && pointersRef.current.size >= 2) {
      const pts = [...pointersRef.current.values()];
      const a = pts[0]!;
      const b = pts[1]!;
      const d = Math.max(1, dist(a, b));
      applyZoom(pinchRef.current.zoom * (d / pinchRef.current.dist), (a.x + b.x) / 2, (a.y + b.y) / 2);
      return;
    }
    const drag = dragRef.current;
    if (!drag) return;
    const next = {
      x: drag.panX + (e.clientX - drag.x),
      y: drag.panY + (e.clientY - drag.y),
    };
    panRef.current = next;
    setPan(next);
  };

  const endPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) {
      pinchRef.current = null;
    }
    if (dragRef.current && e.currentTarget.releasePointerCapture) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* déjà relâché */
      }
    }
    dragRef.current = null;
    setDragging(false);
  };

  const onSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    const z = Number(e.target.value);
    const el = viewportRef.current;
    const rect = el?.getBoundingClientRect();
    const cx = rect ? rect.left + rect.width / 2 : 0;
    const cy = rect ? rect.top + rect.height / 2 : 0;
    applyZoom(z, cx, cy);
  };

  const onDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoomRef.current > ZOOM_MIN + 0.01) {
      applyZoom(ZOOM_MIN, 0, 0);
      return;
    }
    applyZoom(2.4, e.clientX, e.clientY);
  };

  const zoomed = zoom > ZOOM_MIN + 0.01;

  return (
    <div
      ref={viewportRef}
      className={`chat-image-zoom${zoomed ? " is-zoomed" : ""}${dragging ? " is-dragging" : ""}${
        className ? ` ${className}` : ""
      }`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onDoubleClick={onDoubleClick}
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={src}
        alt={alt}
        className="chat-image-zoom__img"
        draggable={false}
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
      />
      <label className="chat-image-zoom__rail">
        <span className="sr-only">Zoom</span>
        <input
          type="range"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step="0.05"
          value={zoom}
          onChange={onSlider}
          onPointerDown={(e) => e.stopPropagation()}
          className="chat-image-zoom__slider"
          aria-valuemin={ZOOM_MIN}
          aria-valuemax={ZOOM_MAX}
          aria-valuenow={Math.round(zoom * 100) / 100}
          aria-label="Zoom de l’image"
        />
      </label>
    </div>
  );
}
